<template>
  <div class="page decontare">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader
      title="Decontare"
      subtitle="Formularele de casare a bunurilor și starea numerelor de inventar."
      icon="mdi-file-sign"
    >
      <template #actions>
        <v-btn v-if="canCreate" class="btn-ghost-light" @click="showSettings = true">
          <v-icon left>mdi-cog-outline</v-icon>Setări
        </v-btn>
        <v-btn v-if="canCreate" class="btn-leaf" @click="showCreateDialog">
          <v-icon left>mdi-plus</v-icon>Formular nou
        </v-btn>
      </template>
    </PageHeader>

    <div class="page-body">
      <section class="panel">
        <div class="panel-toolbar">
          <h2 class="panel-title">
            Formulare de decontare
            <span class="count">{{ filteredData.length }}</span>
          </h2>
          <span class="grow-spacer" />
          <SearchBar @search="handleSearch" />
          <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="decontare" />
        </div>
        <v-data-table
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="filteredData"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="isLoading"
          @pagination="pageStart = $event.pageStart"
          loading-text="Se încarcă formularele..."
          no-data-text="Nu există formulare"
        >
          <template v-slot:item="{ item, index }">
            <tr
              :class="{ 'light-green-row': index % 2 !== 0 }"
              @click="openDetails(item)"
              style="cursor: pointer"
            >
              <td
                v-for="(header, hIndex) in visibleHeaders"
                :key="hIndex"
                :style="header.style ? header.style : {}"
              >
                <template v-if="header.value === 'index'">
                  {{ pageStart + index + 1 }}
                </template>
                <template v-else-if="header.value === 'created_at'">
                  {{ formatDate(item.created_at) }}
                </template>
                <template v-else-if="header.value === 'total_price'">
                  {{ Number(item.total_price).toFixed(2) }}
                </template>
                <template v-else-if="header.value === 'status'">
                  <span class="doc-state" :class="item.status === 'confirmed' ? 'doc-state--done' : 'doc-state--draft'">
                    {{ item.status === 'confirmed' ? 'Confirmat' : 'Ciornă' }}
                  </span>
                </template>
                <template v-else-if="header.value === 'actions'">
                  <div v-if="canDelete(item)" @click.stop>
                    <v-btn icon color="error" aria-label="Șterge formularul" @click="confirmDelete(item)">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  {{ item[header.value] }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </section>
    </div>

    <!-- Create Dialog -->
    <DecontareForm
      v-if="showForm"
      @close="showForm = false"
      @success="handleFormSuccess"
      @error="handleError"
    />

    <!-- Details Dialog -->
    <DecontareDetails
      v-if="showDetails"
      :formId="selectedFormId"
      @close="showDetails = false"
      @updated="fetchData"
      @error="handleError"
    />

    <!-- Settings Dialog -->
    <DecontareSettings
      v-if="showSettings"
      @close="showSettings = false"
      @updated="fetchData"
      @error="handleError"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="dialogDelete" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">Ștergere formular</v-card-title>
        <v-card-text>Sigur doriți să ștergeți acest formular?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="deleteConfirmed">Da</v-btn>
          <v-btn color="red darken-1" text @click="dialogDelete = false">Nu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import PageHeader from "@/components/layout/PageHeader.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import DecontareForm from "./DecontareForm.vue";
import DecontareDetails from "./DecontareDetails.vue";
import DecontareSettings from "./DecontareSettings.vue";
import { mapGetters } from "vuex";

export default {
  name: "DecontarePage",
  components: {
    PageHeader,
    ColumnSelect,
    SearchBar,
    ErrorAlert,
    SuccessAlert,
    DecontareForm,
    DecontareDetails,
    DecontareSettings,
  },
  data() {
    return {
      headers: [],
      tableData: [],
      visibleHeaders: [],
      search: "",
      sortBy: "id",
      sortDesc: true,
      isLoading: true,
      // first row of the current table page, so "Nr" continues across pages
      pageStart: 0,
      errorMessage: "",
      successMessage: "",
      showForm: false,
      showDetails: false,
      showSettings: false,
      selectedFormId: null,
      dialogDelete: false,
      formToDelete: null,
    };
  },
  methods: {
    async fetchData() {
      try {
        this.tableData = await api.getTableData();
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching decontare data:", error);
        this.isLoading = false;
      }
    },
    showCreateDialog() {
      this.showForm = true;
    },
    openDetails(item) {
      this.selectedFormId = item.id;
      this.showDetails = true;
    },
    confirmDelete(item) {
      this.formToDelete = item;
      this.dialogDelete = true;
    },
    async deleteConfirmed() {
      try {
        await api.deleteForm(this.formToDelete.id);
        this.dialogDelete = false;
        this.formToDelete = null;
        this.successMessage = "Formularul a fost șters cu succes";
        await this.fetchData();
      } catch (error) {
        this.dialogDelete = false;
        this.errorMessage = "Eroare la ștergerea formularului";
      }
    },
    // a confirmed form is the record of the write-off: only drafts are deleted
    canDelete(item) {
      const role = this.getUser.role;
      return item.status === 'draft' && (role === 'admin' || role === 'inginer' || role === 'inginer-pc');
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    handleFormSuccess() {
      this.showForm = false;
      this.successMessage = "Formularul a fost creat cu succes";
      this.fetchData();
    },
    handleError(message) {
      this.errorMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    canCreate() {
      const role = this.getUser.role;
      return role === 'inginer' || role === 'inginer-pc' || role === 'admin';
    },
    filteredData() {
      return helper.filteredData(this.tableData, this.search);
    },
  },
  mounted() {
    this.headers = require("./headers.json");
    this.visibleHeaders = this.headers.filter(
      (header) => header.defaultVisible
    );
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible;
    });
    this.$nextTick(() => {
      this.fetchData();
    });
  },
};
</script>

<style scoped>
.doc-state {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.92rem;
}
.doc-state::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.doc-state--done {
  color: var(--green-700);
  background: #dcf1e4;
}
.doc-state--draft {
  color: var(--amber);
  background: var(--amber-bg);
}
</style>
